/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <explanation> */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { QUERIES } from '@/constants/queries'
import { getAllCoursesByCurriculum } from '@/services/education/get-all-courses-by-curriculum'
import { getAllEquivalentCurriculumExternalTransfer } from '@/services/education/get-all-equivalent-curriculum-external-transfer'
import { getPreviousEducationCoursesByRequest } from '@/services/requests/get-previous-education-courses-by-request'
import { getRequestById } from '@/services/requests/get-request-by-id'
import type { Curriculum } from '@/types/courses/curriculum'

interface UseExternalTransferReviewQuery {
  currentCurriculum?: Curriculum
}

export function useExternalTransferReviewQuery({
  currentCurriculum
}: UseExternalTransferReviewQuery) {
  const [allQueriesLoaded, setAllQueriesLoaded] = useState(false)

  const { id: requestIdParam } = useParams()

  const {
    data: request,
    isLoading: isRequestLoading,
    refetch: refetchRequest
  } = useQuery({
    queryKey: [QUERIES.QUERY_GET_REQUEST_BY_ID, requestIdParam],
    queryFn: () => getRequestById(requestIdParam ?? ''),
    enabled: !!requestIdParam
  })

  const {
    data: curriculumCourses,
    isLoading: isCurriculumCoursesLoading,
    isPending: isPendingCurriculumCourses
  } = useQuery({
    queryKey: [QUERIES.QUERY_GET_COURSES_BY_CURRICULUM, currentCurriculum],
    queryFn: () => getAllCoursesByCurriculum(currentCurriculum?.id),
    enabled: !!currentCurriculum
  })

  const { data: previousCourses, isLoading: isPreviouCoursesLoading } =
    useQuery({
      queryKey: [
        QUERIES.QUERY_GET_PREVIOUS_EDUCATION_COURSES_BY_REQUEST,
        requestIdParam
      ],
      queryFn: () => getPreviousEducationCoursesByRequest(requestIdParam),
      enabled: !!requestIdParam
    })

  const { data: allCurriculums, isLoading: isAllCurriculumsLoading } = useQuery(
    {
      queryKey: [QUERIES.QUERY_GET_CURRICULUMS, request],
      queryFn: () =>
        getAllEquivalentCurriculumExternalTransfer(request?.student.curriculum),
      enabled: !!request
    }
  )

  useEffect(() => {
    if (!allQueriesLoaded) {
      if (
        !isAllCurriculumsLoading
        && !isPreviouCoursesLoading
        && !isCurriculumCoursesLoading
        && !isRequestLoading
      ) {
        setAllQueriesLoaded(true)
      }
    }
  }, [
    isAllCurriculumsLoading,
    isPreviouCoursesLoading,
    isCurriculumCoursesLoading,
    isRequestLoading
  ])

  return {
    states: {
      request,
      curriculumCourses,
      previousCourses,
      allCurriculums,
      allQueriesLoaded,
      isPendingCurriculumCourses
    },
    actions: {
      refetchRequest
    }
  }
}
